import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { parseQuotationFee, parseQuotationPerTime, calculateQuotationTotal } from "../quotationPricing";

const PAGE_WIDTH_PX = 794;
const PAGE_HEIGHT_PX = 1123;
const CANVAS_SCALE = 5;

const formatVnd = (amount) => Math.round(Number(amount) || 0).toLocaleString("en-US");

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const getBilingualTitle = (item) => {
  const title = item?.title ?? item?.name ?? item?.serviceName ?? item?.serviceItemName ?? item?.label ?? item;
  if (!title) return { vi: "", en: "" };
  if (typeof title === "string") return { vi: title, en: title };
  return { vi: title.vi || title.en || "", en: title.en || title.vi || "" };
};

export const buildQuotationPdfTableRows = (quotationItems = [], defaultDistanceInKm = "") => {
  const rows = [];
  let rowNum = 0;

  quotationItems.forEach((item) => {
    const { vi, en } = getBilingualTitle(item.serviceItemData || item.serviceItemTitle || item.serviceName);
    const perTime = parseQuotationPerTime(item.perTime);
    const serviceFee = parseQuotationFee(item.unitFee);
    const transportationFee = parseQuotationFee(item.transportationFee);
    const adjustment = parseQuotationFee(item.adjustment);
    const distance = item.distanceInKm !== "" && item.distanceInKm != null ? item.distanceInKm : defaultDistanceInKm;

    if (vi || en || serviceFee > 0) {
      rowNum += 1;
      // Service fee column shows the total for this line (unit rate × per-time), matching
      // what CMD's Quotation Details table displays — not just the per-unit rate.
      const totalServiceFee = serviceFee * perTime;
      const subtotal = totalServiceFee + transportationFee + adjustment;
      rows.push({
        no: rowNum,
        serviceNameVi: vi || en || "",
        serviceNameEn: en && en !== vi ? en : "",
        perTime: perTime || 1,
        distance: distance ? `${distance}km` : "",
        serviceFee: totalServiceFee > 0 ? formatVnd(totalServiceFee) : "0",
        transportationFee: transportationFee > 0 ? formatVnd(transportationFee) : "0",
        adjustment: adjustment > 0 ? formatVnd(adjustment) : "0",
        subtotal: subtotal > 0 ? formatVnd(subtotal) : "0",
      });
    }
  });

  return rows;
};

const getTransportationRates = (config) => {
  const firstKm = config?.firstKm ?? 2;
  const taxiWorking = config?.transportFees?.taxi?.workingHours?.opdInternationalInsured ?? {};
  const baseFare = taxiWorking.baseFare ?? 100000;
  const perKmRate = taxiWorking.perKmRate ?? 15000;
  return { firstKm, baseFare, perKmRate };
};

const formatQuotationDate = (date) => {
  const d = date.getDate().toString().padStart(2, "0");
  const m = (date.getMonth() + 1).toString().padStart(2, "0");
  const y = date.getFullYear();
  return `${d}/${m}/${y}`;
};

const formatQuotationTime = (date) =>
  date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });

const formatQuotationDateTime = (date) => {
  const d = formatQuotationDate(date);
  const t = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  return `${d} ${t}`;
};

const STYLES = `
  .qp-page {
    width: ${PAGE_WIDTH_PX}px;
    height: ${PAGE_HEIGHT_PX}px;
    background: #fff;
    color: #000;
    font-family: "Arial Narrow", "Segoe UI Condensed", "Roboto Condensed", Arial, sans-serif;
    font-size: 8.5px;
    line-height: 1.3;
    box-sizing: border-box;
    padding: 20px 30px 20px;
    position: relative;
    overflow: hidden;
  }
  .qp-page * { box-sizing: border-box; margin: 0; padding: 0; }
  .qp-page strong, .qp-page b { font-weight: 700 !important; }
  
  .qp-header { display: flex; align-items: flex-start; margin-bottom: 6px; }
  .qp-logo { width: 130px; height: auto; flex-shrink: 0; }
  .qp-header-center { flex: 1; text-align: center; padding-top: 2px; }
  .qp-header-center .qp-h1 { font-size: 11px; font-weight: 700; color: #000; line-height: 1.25; }
  .qp-header-center .qp-h2 { font-size: 10px; font-weight: 700; color: #000; margin-top: 1px; }
  .qp-header-center .qp-h3 { font-size: 9.5px; font-weight: 700; color: #000; margin-top: 2px; }
  .qp-header-center .qp-h4 { font-size: 9.5px; font-weight: 700; color: #000; margin-top: 1px; }
  
  .qp-section-bar {
    background: #a2d5c6;
    color: #000;
    font-size: 9.5px;
    font-weight: 700;
    padding: 3px 8px;
    letter-spacing: 0.2px;
    border: 1px solid #000;
    border-bottom: none;
  }
  
  .qp-patient-box {
    border: 1px solid #000;
    margin-bottom: 8px;
  }
  .qp-patient-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    border-bottom: 1px solid #000;
  }
  .qp-patient-grid:last-child { border-bottom: none; }
  .qp-field { padding: 4px 6px; border-right: 1px solid #000; min-height: 34px; }
  .qp-field:last-child { border-right: none; }
  .qp-field-full { grid-column: 1 / -1; border-right: none; border-bottom: 1px solid #000; padding: 4px 6px; }
  .qp-label-vi { font-size: 8.5px; color: #000; font-weight: 700; }
  .qp-label-en { font-size: 7.5px; color: #404040; font-weight: 400; }
  .qp-value { font-size: 9.5px; font-weight: 700; margin-top: 1px; word-break: break-word; }
  
  .qp-fee-section {
    display: flex;
    gap: 15px;
    margin-bottom: 10px;
    font-size: 8.5px;
    line-height: 1.35;
  }
  .qp-fee-left {
    width: 140px;
    flex-shrink: 0;
  }
  .qp-fee-left-vi { font-size: 8.5px; color: #000; font-weight: 700; }
  .qp-fee-left-en { font-size: 8px; color: #404040; font-weight: 400; margin-top: 70px; }
  .qp-fee-left-trans-vi { font-size: 8.5px; color: #000; font-weight: 700; margin-top: 90px; }
  .qp-fee-left-trans-en { font-size: 8px; color: #404040; font-weight: 400; margin-top: 20px; }

  .qp-fee-right { flex: 1; }
  .qp-fee-vi-block { color: #000; font-weight: 700; }
  .qp-fee-en-block { color: #404040; font-weight: 400; margin-top: 8px; }

  .qp-note-title { margin-top: 3px; margin-bottom: 2px; text-decoration: underline; font-weight: 700; color: #000; }
  .qp-note-title-en { margin-top: 4px; margin-bottom: 2px; text-decoration: underline; font-weight: 400; color: #404040; }

  .qp-check-group { margin-left: 28px; }
  .qp-check-row { display: flex; gap: 6px; margin-bottom: 2px; align-items: flex-start; }
  .qp-check-mark { flex-shrink: 0; width: 12px; font-size: 8.5px; text-align: center; }

  .qp-trans-grid { display: flex; justify-content: space-between; margin-top: 6px; }
  .qp-trans-left-vi { color: #000; font-weight: 700; }
  .qp-trans-right-vi { text-align: right; color: #000; font-weight: 700; }
  .qp-trans-left-en { color: #404040; font-weight: 400; }
  .qp-trans-right-en { text-align: right; color: #404040; font-weight: 400; }

  .qp-table { width: 100%; border-collapse: collapse; margin-bottom: 0; font-size: 8.5px; }
  .qp-table th, .qp-table td { border: 1px solid #000; padding: 4px 5px; vertical-align: middle; text-align: center; }
  .qp-table th { background: #a2d5c6; font-weight: 700; font-size: 8px; line-height: 1.25; color: #000; }
  .qp-table th .en-header { font-weight: 400; color: #595959; display: block; font-size: 7.5px; }
  .qp-table td.desc { text-align: left; }
  .qp-table .desc-vi { font-size: 8.5px; color: #000; font-weight: 700; }
  .qp-table .desc-en { font-size: 7.5px; color: #404040; font-weight: 400; }
  .qp-total-row td { background: #a2d5c6; font-weight: 700; font-size: 9px; border: 1px solid #000; }
  .qp-total-row .total-label { text-align: left; padding-left: 8px; font-weight: 700; }
  .qp-total-row .total-value { text-align: right; padding-right: 8px; font-weight: 700; }

  .qp-ack-box { border: 1px solid #000; margin-top: 8px; }
  .qp-ack-bar { background: #a2d5c6; padding: 3px 8px; font-weight: 700; font-size: 9px; border-bottom: 1px solid #000; color: #000; }
  .qp-ack-body { padding: 5px 8px; font-size: 8.5px; }
  .qp-ack-names { display: flex; margin-top: 6px; }
  .qp-ack-name-box { flex: 1; text-align: center; padding: 2px; }
  .qp-ack-name-box .name { background: #a2d5c6; padding: 3px; font-weight: 700; font-size: 9px; min-height: 20px; display: flex; align-items: center; justify-content: center; border: 1px solid #000; color: #000; }
  .qp-ack-name-box .label { font-size: 7.5px; margin-top: 3px; color: #000; line-height: 1.25; font-weight: 700; }
  .qp-ack-name-box .label-en { color: #404040; font-weight: 400; }
  .qp-ack-name-box:first-child { border-right: 1px solid #000; padding-right: 6px; }
  .qp-ack-name-box:last-child { padding-left: 6px; }

  .qp-sig-table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 8.5px; }
  .qp-sig-table th { background: #a2d5c6; border: 1px solid #000; padding: 4px; font-weight: 700; text-align: center; line-height: 1.25; color: #000; }
  .qp-sig-table td { border: 1px solid #000; height: 50px; vertical-align: bottom; padding: 4px; text-align: center; }
  .qp-sig-table .sig-footer { background: #e8e8e8; font-size: 7.5px; height: auto; padding: 3px; text-align: center; color: #000; }
  .qp-sig-table .sig-footer-green { background: #a2d5c6; font-weight: 700; }
  .qp-sig-datetime { font-size: 9.5px; font-weight: 700; margin-bottom: 4px; }

  .qp-blue-heading { color: #0070c0 !important; font-weight: 700; font-size: 9.5px; margin-top: 10px; margin-bottom: 4px; }

  .qp-note-block { margin-bottom: 8px; font-size: 8.5px; line-height: 1.35; }
  .qp-note-block .vi-para { color: #000; font-weight: 700; }
  .qp-note-block .en-para { color: #404040; font-weight: 400; margin-top: 2px; }

  .qp-cancel-block { margin-bottom: 6px; font-size: 8.5px; line-height: 1.35; }
  .qp-cancel-block .vi-para { color: #000; font-weight: 700; }
  .qp-cancel-block .en-para { color: #404040; font-weight: 400; margin-top: 2px; }

  .qp-indent-group { padding-left: 16px; margin-top: 3px; margin-bottom: 4px; }
  .qp-indent-group .vi-para { color: #000; font-weight: 700; }
  .qp-indent-group .en-para { color: #404040; font-weight: 400; margin-top: 1px; }

  .qp-contact-footer { color: #0070c0 !important; font-size: 8.5px; line-height: 1.4; margin-top: 10px; font-weight: 700; }
  .qp-contact-footer .en-footer { font-weight: 400; margin-top: 1px; margin-bottom: 3px; display: block; color: #404040 !important; }
  .qp-contact-footer-list { padding-left: 16px; font-weight: normal; color: #000; line-height: 1.35; margin-top: 3px; }

  .qp-footer {
    position: absolute;
    bottom: 12px;
    left: 30px;
    right: 30px;
    border-top: 1px solid #000;
    padding-top: 3px;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    font-size: 7.5px;
    color: #000;
  }
  .qp-footer-center { text-align: center; flex: 1; }
  .qp-footer-right { text-align: right; line-height: 1.25; }
`;

const renderHeader = (logoSrc) => `
  <div class="qp-header">
    <img class="qp-logo" src="${escapeHtml(logoSrc)}" alt="FV Hospital" crossorigin="anonymous" />
    <div class="qp-header-center">
      <div class="qp-h1"><strong>BỆNH VIỆN FV-TRUNG TÂM KHÁM BỆNH TỪ XA &amp; CHĂM SÓC TẠI NHÀ</strong></div>
      <div class="qp-h2"><strong>BÁO GIÁ DỊCH VỤ DỰ KIẾN</strong></div>
      <div class="qp-h3"><strong>FV HEALTHCARE AT HOME &amp; TELEMEDICINE CENTRE</strong></div>
      <div class="qp-h4"><strong>ESTIMATED SERVICE QUOTATION</strong></div>
    </div>
  </div>
`;

const formatPatientDob = (dob) => {
  if (!dob) return "";
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return dob;
  return formatQuotationDate(d);
};

const renderPatientInfo = (patient, quotationDate, quotationTime) => `
  <div class="qp-section-bar"><strong>PATIENT INFORMATION</strong></div>
  <div class="qp-patient-box">
    <div class="qp-patient-grid">
      <div class="qp-field">
        <div class="qp-label-vi"><strong>Họ và tên BN :</strong></div>
        <div class="qp-label-en">Patient fullname</div>
        <div class="qp-value"><strong>${escapeHtml(patient.name)}</strong></div>
      </div>
      <div class="qp-field">
        <div class="qp-label-vi"><strong>Mã bệnh nhân:</strong></div>
        <div class="qp-label-en">Hospital number:</div>
        <div class="qp-value"><strong>${escapeHtml(patient.hnNumber)}</strong></div>
      </div>
      <div class="qp-field">
        <div class="qp-label-vi"><strong>Ngày lập báo giá:</strong></div>
        <div class="qp-label-en">Quotation date</div>
        <div class="qp-value"><strong>${escapeHtml(quotationDate)}</strong></div>
        <div class="qp-value" style="font-weight:700;font-size:8.5px;">${escapeHtml(quotationTime)}</div>
      </div>
    </div>
    <div class="qp-patient-grid">
      <div class="qp-field">
        <div class="qp-label-vi"><strong>Số điện thoại:</strong></div>
        <div class="qp-label-en">Phone number</div>
        <div class="qp-value"><strong>${escapeHtml(patient.phone)}</strong></div>
      </div>
      <div class="qp-field">
        <div class="qp-label-vi"><strong>Ngày sinh:</strong></div>
        <div class="qp-label-en">Date of birth</div>
        <div class="qp-value"><strong>${escapeHtml(formatPatientDob(patient.dob))}</strong></div>
      </div>
      <div class="qp-field">
        <div class="qp-label-vi"><strong>Giới tính:</strong></div>
        <div class="qp-label-en">Gender</div>
        <div class="qp-value"><strong>${escapeHtml(patient.gender || "")}</strong></div>
      </div>
    </div>
    <div class="qp-patient-grid">
      <div class="qp-field-full">
        <div class="qp-label-vi"><strong>Địa chỉ cung cấp dịch vụ :</strong></div>
        <div class="qp-label-en">Service address</div>
        <div class="qp-value"><strong>${escapeHtml(patient.address)}</strong></div>
      </div>
    </div>
  </div>
`;

const renderFooter = (requestId, pageNum, totalPages) => `
  <div class="qp-footer">
    <div>${escapeHtml(requestId)} (v3)</div>
    <div class="qp-footer-center">Page ${pageNum} of ${totalPages}</div>
    <div class="qp-footer-right">
      Bệnh viện FV - Trung Tâm Khám Bệnh Từ Xa và Chăm Sóc tại Nhà<br/>
      FV healthcare at Home &amp; Telemedicine Centre
    </div>
  </div>
`;

const renderTableHeader = () => `
  <thead>
    <tr>
      <th style="width:5%">
        <strong>STT</strong>
        <span class="en-header">No.</span>
      </th>

      <th style="width:25%">
        <strong>Tên dịch vụ</strong>
        <span class="en-header">Service name</span>
      </th>

      <th style="width:10%">
        <strong>Số lần</strong>
        <span class="en-header">Per time</span>
      </th>

      <th style="width:10%">
        <strong>Khoảng cách</strong>
        <span class="en-header">Distance</span>
      </th>

      <th style="width:13%">
        <strong>Phí dịch vụ</strong>
        <span class="en-header">Service fee</span>
      </th>

      <th style="width:13%">
        <strong>Phí di chuyển</strong>
        <span class="en-header">Transportation fee</span>
      </th>

      <th style="width:12%">
        <strong>Phụ phí</strong>
        <span class="en-header">Adjustment</span>
      </th>

      <th style="width:12%">
        <strong>Tạm tính</strong>
        <span class="en-header">Subtotal</span>
      </th>
    </tr>
  </thead>
`;

const renderTableRow = (row) => `
  <tr>
    <td>${row.no}</td>
    <td class="desc">
      <div class="desc-vi"><strong>${escapeHtml(row.serviceNameVi)}</strong></div>
      ${row.serviceNameEn ? `<div class="desc-en">${escapeHtml(row.serviceNameEn)}</div>` : ""}
    </td>
    <td>${row.perTime}</td>
    <td>${escapeHtml(row.distance)}</td>
    <td>${row.serviceFee}</td>
    <td>${row.transportationFee}</td>
    <td>${row.adjustment}</td>
    <td>${row.subtotal}</td>
  </tr>
`;

const renderFeeParameters = (distanceInKm, rates) => `
  <div class="qp-fee-section">
    <div class="qp-fee-left">
      <div class="qp-fee-left-vi"><strong>Cơ sở tính phí</strong></div>
      <div class="qp-fee-left-en">Fee parameter</div>
      <div class="qp-fee-left-trans-vi"><strong>Biểu phí di chuyển:</strong></div>
      <div class="qp-fee-left-trans-en">Transportation fee structure:</div>
    </div>
    <div class="qp-fee-right">
      <div class="qp-fee-vi-block"><strong>Cấu trúc chi phí dịch vụ tại nhà bao gồm: Phí dịch vụ y tế cơ bản và phí di chuyển. Trong đó, phí di chuyển được tính toán dựa trên khoảng cách thực tế (theo dữ liệu Google Maps) từ Bệnh viện FV đến địa điểm của khách hàng.</strong></div>
      <div class="qp-note-title"><u>Lưu ý:</u></div>
      <div class="qp-check-group">
        <div class="qp-check-row qp-fee-vi-block">
          <span class="qp-check-mark"><strong>&#10003;</strong></span>
          <span><strong>Bảng phí dưới đây áp dụng trong giờ hành chính. Đối với các dịch vụ thực hiện ngoài giờ hoặc vào cuối tuần, phụ phí 70,000 VNĐ/lần sẽ được áp dụng.</strong></span>
        </div>
        <div class="qp-check-row qp-fee-vi-block">
          <span class="qp-check-mark"><strong>&#10003;</strong></span>
          <span><strong>Giá dịch vụ có thể được điều chỉnh mà không cần báo trước.</strong></span>
        </div>
      </div>
      
      <div class="qp-fee-en-block">Our Homecare Service fees comprise a base medical service fee and a transportation charge. The transportation fee is calculated based on the actual travel distance (via Google Maps) from FV Hospital to the requested location.</div>
      <div class="qp-note-title-en"><u>Note:</u></div>
      <div class="qp-check-group">
        <div class="qp-check-row qp-fee-en-block">
          <span class="qp-check-mark">&#10003;</span>
          <span>The rates listed below are applicable during standard working hours. A surcharge of 70,000 VND per visit will apply to services provided after hours or on weekends.</span>
        </div>
        <div class="qp-check-row qp-fee-en-block">
          <span class="qp-check-mark">&#10003;</span>
          <span>Service fees are subject to change without prior notice.</span>
        </div>
      </div>

      <div class="qp-trans-grid">
        <div class="qp-trans-left-vi">
          <div><strong>Giá cước tối thiểu ${rates.firstKm}km đầu tiên = ${formatVnd(rates.baseFare)}đ.</strong></div>
          <div><strong>Giá cước mỗi km tiếp theo = ${formatVnd(rates.perKmRate)}.00đ/1 km</strong></div>
        </div>
      </div>
      <div class="qp-trans-grid" style="margin-top: 2px;">
        <div class="qp-trans-left-en">
          <div>Initial rate (for the first ${rates.firstKm} km): ${formatVnd(rates.baseFare)}.00 VND.</div>
          <div>Additional distance rate: ${formatVnd(rates.perKmRate)}.00 VND/km</div>
        </div>
      </div>
    </div>
  </div>
`;

const renderAcknowledgement = (patientName, consultantName) => `
  <div class="qp-ack-box">
    <div class="qp-ack-bar"><strong>Xác nhận / Acknowledgement</strong></div>
    <div class="qp-ack-body">
      <div style="font-weight:700; color:#000;">Tôi xác nhận, tôi đã được Bệnh viện FV tư vấn về chi phí</div>
      <div style="margin-top:2px; color:#404040;">I acknowledge that I have received Financial Counselling from FV Hospital</div>
      <div class="qp-ack-names">
        <div class="qp-ack-name-box">
          <div class="name"><strong>${escapeHtml(patientName)}</strong></div>
          <div class="label"><strong>Tên Bệnh nhân/ Người được tư vấn</strong><br/><span class="label-en">Name of the Patient/ Person Counselled</span></div>
        </div>
        <div class="qp-ack-name-box">
          <div class="name"><strong>${escapeHtml(consultantName)}</strong></div>
          <div class="label"><strong>Tên của người tư vấn</strong><br/><span class="label-en">Name of the Consultant</span></div>
        </div>
      </div>
    </div>
  </div>
`;

const renderPage1 = ({ logoSrc, patient, request, tableRows, grandTotal, rates, consultantName, quotationDate, quotationTime, pageNum, totalPages }) => `
  <div class="qp-page" data-page="${pageNum}">
    ${renderHeader(logoSrc)}
    ${renderPatientInfo(patient, quotationDate, quotationTime)}
    ${renderFeeParameters(request.distanceInKm, rates)}
    <div style="margin-bottom: 8px; font-size: 8.5px;">
      <div style="color: #000; font-weight: 700;">Quãng đường ước tính (km): ${escapeHtml(request.distanceInKm)}</div>
      <div style="color: #404040; font-weight: 400;">Estimated distance by km</div>
    </div>
    <table class="qp-table">
      ${renderTableHeader()}
      <tbody>
        ${tableRows.map(renderTableRow).join("")}
        <tr class="qp-total-row">
          <td colspan="7" class="total-label"><strong>TỔNG PHÍ ƯỚC TÍNH/ TOTAL ESTIMATED FEE</strong></td>
          <td class="total-value"><strong>${formatVnd(grandTotal)}</strong></td>
        </tr>
        <tr class="qp-total-row">
          <td colspan="7" class="total-label"><strong>TỔNG PHÍ ƯỚC TÍNH/ TOTAL ESTIMATED FEE</strong></td>
          <td class="total-value"><strong>${formatVnd(grandTotal)}</strong></td>
        </tr>
      </tbody>
    </table>
    ${renderAcknowledgement(patient.name, consultantName)}
    ${renderFooter(request.requestId, pageNum, totalPages)}
  </div>
`;

const renderSignatureSection = (relationship, counsellingDateTime) => `
  <table class="qp-sig-table">
    <thead>
      <tr>
        <th><strong>Chữ ký bệnh nhân/ Người được tư vấn</strong><br/><span style="font-weight: 400; color: #595959;">Signature of Patient/Person Counselled</span></th>
        <th><strong>Tên và chữ ký của người tư vấn</strong><br/><span style="font-weight: 400; color: #595959;">Name and Signature of the Consultant</span></th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td></td>
        <td><div class="qp-sig-datetime"><strong>${escapeHtml(counsellingDateTime)}</strong></div></td>
      </tr>
      <tr>
        <td class="sig-footer">Relationship to Patient &amp; Contact Number<br/>Mối quan hệ với bệnh nhân và số điện thoại liên hệ<br/>${escapeHtml(relationship || "-")}</td>
        <td class="sig-footer sig-footer-green"><strong>Date &amp; Time of Financial Counselling</strong><br/>Ngày &amp; giờ tư vấn</td>
      </tr>
    </tbody>
  </table>
`;

const renderImportantNotes = () => `
  <div class="qp-blue-heading"><strong>Lưu ý quan trọng/ Important Notes</strong></div>
  
  <div class="qp-note-block">
    <div class="vi-para"><strong>1.Số lượng nhân viên: Mỗi dịch vụ chăm sóc tại nhà mặc định được thực hiện bởi 01 nhân viên y tế. Nếu tính chất ca bệnh yêu cầu 02 người, chúng tôi sẽ thông báo phí phát sinh cho bệnh nhân trước khi tiến hành</strong></div>
    <div class="en-para">Personnel: Standard services include one healthcare professional. Should the care requirements necessitate two persons, additional fees will be communicated to the patient for prior approval</div>
  </div>

  <div class="qp-note-block">
    <div class="vi-para"><strong>2.Để đảm bảo chất lượng chuyên môn và tính liên tục của dịch vụ chăm sóc tại nhà, Bệnh viện FV điều phối nhân viên y tế theo hình thức luân phiên và không cung cấp đội ngũ cố định duy nhất. Do đó, việc chỉ định các cá nhân/nhân viên cụ thể theo yêu cầu sẽ không được cam kết. Chính sách này được áp dụng nhằm duy trì tiêu chuẩn cao trong việc chăm sóc người bệnh, đồng thời đảm bảo tuân thủ các quy định về sức khỏe, phúc lợi và chế độ nghỉ ngơi của nhân viên y tế.</strong></div>
    <div class="en-para">To ensure clinical quality and the continuity of our homecare services, FV Hospital coordinates healthcare staff on a rotational basis and does not provide a fixed-only team. Consequently, requests for the assignment of specific individuals/staff cannot be guaranteed. This policy is in place to maintain high standards of patient care while ensuring compliance with regulations regarding staff health, well-being, and rest requirements.</div>
  </div>

  <div class="qp-note-block">
    <div class="vi-para"><strong>3.Dịch vụ chăm sóc tại nhà của chúng tôi được thực hiện theo hình thức đặt hẹn trước và thanh toán trước. Để đảm bảo công tác chuẩn bị nhân lực chu đáo và chất lượng chăm sóc tốt nhất, Quý khách vui lòng đặt hẹn và xác nhận dịch vụ ít nhất 48 giờ trước thời điểm dự kiến</strong></div>
    <div class="en-para">FV Homecare services are provided on a pre-booked and pre-paid basis. To ensure thorough staffing arrangements and the highest quality of care, please book and confirm your service at least 48 hours before the scheduled time.</div>
  </div>

  <div class="qp-blue-heading"><strong>Hủy" hoặc "Đổi" lịch hẹn chăm sóc tại nhà/ "Cancellation" or "Rescheduling" of a home care appointment.</strong></div>

  <div class="qp-cancel-block">
    <div class="vi-para"><strong>Thời gian thông báo: Quý khách vui lòng thông báo cho Bệnh viện FV ít nhất 48 giờ trước giờ hẹn nếu có nhu cầu thay đổi hoặc hủy lịch. Trong trường hợp Quý khách hủy lịch khi nhân viên y tế đã có mặt tại điểm hẹn, phí di chuyển của nhân viên sẽ được tính vào chi phí và Quý khách sẽ được hoàn lại phần phí dịch vụ còn lại theo quy định của Bệnh viện.</strong></div>
    <div class="en-para">Notification Period: Please notify FV Hospital at least 48 hours prior to your scheduled appointment for any changes or cancellations. In the event of a cancellation after our staff has arrived at the service location, a travel fee will be charged. Only the remaining service fee will be refunded in accordance with the hospital's policy.</div>
  </div>

  <div class="qp-cancel-block">
    <div class="vi-para"><strong style="color: #0070c0;">Thông tin liên hệ thay đổi/hủy lịch/Contact Information for changes/cancellations:</strong></div>
    <div class="qp-indent-group">
      <div class="vi-para"><strong>Trong giờ hành chính (Thứ Hai - Thứ Sáu: 8:00 – 16:45; Thứ Bảy: 8:00 – 11:45) Vui lòng gọi số điện thoại của Trung tâm: 09 6262 7826</strong></div>
      <div class="vi-para"><strong>hoặc 028 3511 3389</strong></div>
      <div class="en-para">During working hours (Monday - Friday: 8:00 AM - 4:45 PM; Saturday: 8:00 AM - 11:45 AM) Please call our Centre cellphone at (+84) 9 6262 7826</div>
      <div class="en-para">or Direct line: (+84) 28 3511 3389</div>
    </div>
    <div class="qp-indent-group">
      <div class="vi-para"><strong>Ngoài giờ hành chính, cuối tuần và ngày lễ: xin vui lòng liên hệ Lễ Tân Khoa Cấp Cứu của Bệnh viện qua số: 028. 3511. 3500</strong></div>
      <div class="en-para">Outside working hours, weekends, or public holidays: Please contact the Emergency Department Reception at (+84) 28 3511 3500</div>
    </div>
  </div>

  <div class="qp-contact-footer">
    <strong>Khi cần hỗ trợ và tư vấn thêm thông tin, quý khách vui lòng liên hệ chúng tôi trong khung giờ hành chính Thứ Hai-Thứ Sáu: 8:00-16:45 ;<br/>
    Thứ Bảy: 8:00-11:45 qua:</strong><br/>
    <span class="en-footer">Should you need any information, please contact us in working hours Mon-Fri: 8AM-16:45PM; Sat: 8AM-11:45AM via:</span>
    <div class="qp-contact-footer-list">
      <div><strong>Di động/Cellphone:</strong> (+84) 9.6262.7826 (Zalo/Viber/WhatsApp)</div>
      <div><strong>Điện thoại bàn/Telephone:</strong> (+84) 28 3511 3389</div>
      <div><strong>Hộp thư điện tử/ Email:</strong> chamsocytetainha@fvhospital.com hoặc/ or telemedicine@fvhospital.com</div>
    </div>
  </div>
`;

const renderPage2 = ({ logoSrc, patient, request, relationship, counsellingDateTime, quotationDate, quotationTime, pageNum, totalPages }) => `
  <div class="qp-page" data-page="${pageNum}">
    ${renderHeader(logoSrc)}
    ${renderPatientInfo(patient, quotationDate, quotationTime)}
    ${renderSignatureSection(relationship, counsellingDateTime)}
    ${renderImportantNotes()}
    ${renderFooter(request.requestId, pageNum, totalPages)}
  </div>
`;

const renderPageToCanvas = async (pageElement) => {
  return html2canvas(pageElement, {
    scale: CANVAS_SCALE,
    useCORS: true,
    logging: false,
    allowTaint: true,
    backgroundColor: "#ffffff",
    width: PAGE_WIDTH_PX,
    height: PAGE_HEIGHT_PX,
    windowWidth: PAGE_WIDTH_PX,
    windowHeight: PAGE_HEIGHT_PX,
    imageTimeout: 0,
  });
};

export const generateQuotationPdf = async ({
  logoSrc,
  patient = {},
  request = {},
  quotationItems = [],
  transportationFeeConfig = null,
  consultantName = "",
  relationship = "",
  fileName = "quotation.pdf",
}) => {
  const asPricingItem = (item) => ({
    serviceFee: item.unitFee,
    transportFee: item.transportationFee,
    additionalFee: item.adjustment,
    perTime: item.perTime,
  });
  const grandTotal = calculateQuotationTotal(quotationItems.map(asPricingItem));
  const tableRows = buildQuotationPdfTableRows(quotationItems, request.distanceInKm);
  const rates = getTransportationRates(transportationFeeConfig);

  const now = new Date();
  const quotationDate = formatQuotationDate(now);
  const quotationTime = formatQuotationTime(now);
  const counsellingDateTime = formatQuotationDateTime(now);

  const totalPages = 2;
  const page1Html = renderPage1({
    logoSrc,
    patient,
    request,
    tableRows,
    grandTotal,
    rates,
    consultantName,
    quotationDate,
    quotationTime,
    pageNum: 1,
    totalPages,
  });
  const page2Html = renderPage2({
    logoSrc,
    patient,
    request,
    relationship,
    counsellingDateTime,
    quotationDate,
    quotationTime,
    pageNum: 2,
    totalPages,
  });

  const container = document.createElement("div");
  container.style.cssText = "position:fixed;left:-9999px;top:0;z-index:-1;";
  container.innerHTML = `<style>${STYLES}</style>${page1Html}${page2Html}`;
  document.body.appendChild(container);

  const pages = container.querySelectorAll(".qp-page");
  const pdf = new jsPDF("p", "mm", "a4");
  const pageWidthMm = 210;
  const pageHeightMm = 297;

  try {
    for (let i = 0; i < pages.length; i += 1) {
      const canvas = await renderPageToCanvas(pages[i]);
      const imgData = canvas.toDataURL("image/png", 1.0);
      if (i > 0) pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, 0, pageWidthMm, pageHeightMm, undefined, "FAST");
    }
    pdf.save(fileName);
  } finally {
    document.body.removeChild(container);
  }
};

export default generateQuotationPdf;
